import type { KataId } from '../../data/catalog/types'
import { getDb } from '../db'
import {
  emptyNoteDocument,
  isNoteDocumentEmpty,
  plainTextToNoteDocument,
  type LegacyPlainNote,
  type NoteDocument,
} from '../notes/document'
import {
  personalRecordId,
  type Favourite,
  type TechniqueNote,
} from '../schema'

function nowIso(): string {
  return new Date().toISOString()
}

type StoredNote = TechniqueNote | LegacyPlainNote

function isLegacyPlainNote(raw: StoredNote): raw is LegacyPlainNote {
  return 'body' in raw && typeof (raw as LegacyPlainNote).body === 'string' && !('content' in raw)
}

/** Normalize legacy plain-text notes to BlockNote documents. */
export function normalizeStoredNote(raw: StoredNote): TechniqueNote {
  if (isLegacyPlainNote(raw)) {
    return {
      id: raw.id,
      kataId: raw.kataId as TechniqueNote['kataId'],
      techniqueId: raw.techniqueId,
      content: plainTextToNoteDocument(raw.body),
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
    }
  }
  const note = raw as TechniqueNote
  return {
    ...note,
    content: Array.isArray(note.content) ? note.content : emptyNoteDocument(),
  }
}

export async function getNote(
  kataId: KataId,
  techniqueId: string,
): Promise<TechniqueNote | undefined> {
  const db = await getDb()
  const raw = (await db.get('notes', personalRecordId(kataId, techniqueId))) as
    | StoredNote
    | undefined
  if (!raw) return undefined
  const normalized = normalizeStoredNote(raw)
  // Lazily rewrite legacy rows so exports stay on schema v3.
  if (isLegacyPlainNote(raw)) {
    await db.put('notes', normalized)
  }
  return normalized
}

export async function listNotesForKata(kataId: KataId): Promise<TechniqueNote[]> {
  const db = await getDb()
  const rows = (await db.getAllFromIndex('notes', 'by-kata', kataId)) as StoredNote[]
  const notes: TechniqueNote[] = []
  for (const raw of rows) {
    const normalized = normalizeStoredNote(raw)
    if (isLegacyPlainNote(raw)) {
      await db.put('notes', normalized)
    }
    notes.push(normalized)
  }
  return notes
}

/**
 * Save a BlockNote document. Empty documents delete the note.
 * Returns null when deleted; never claims success before persistence.
 */
export async function saveNote(
  kataId: KataId,
  techniqueId: string,
  content: NoteDocument,
): Promise<TechniqueNote | null> {
  const db = await getDb()
  const id = personalRecordId(kataId, techniqueId)

  if (isNoteDocumentEmpty(content)) {
    await db.delete('notes', id)
    return null
  }

  const existingRaw = (await db.get('notes', id)) as StoredNote | undefined
  const existing = existingRaw ? normalizeStoredNote(existingRaw) : undefined
  const timestamp = nowIso()
  const record: TechniqueNote = {
    id,
    kataId,
    techniqueId,
    content,
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
  }
  await db.put('notes', record)
  return record
}

export async function deleteNote(kataId: KataId, techniqueId: string): Promise<void> {
  const db = await getDb()
  await db.delete('notes', personalRecordId(kataId, techniqueId))
}

export async function getFavourite(
  kataId: KataId,
  techniqueId: string,
): Promise<Favourite | undefined> {
  const db = await getDb()
  return db.get('favourites', personalRecordId(kataId, techniqueId))
}

export async function listFavouritesForKata(kataId: KataId): Promise<Favourite[]> {
  const db = await getDb()
  return db.getAllFromIndex('favourites', 'by-kata', kataId)
}

export async function setFavourite(
  kataId: KataId,
  techniqueId: string,
  favourite: boolean,
): Promise<Favourite | null> {
  const db = await getDb()
  const id = personalRecordId(kataId, techniqueId)
  if (!favourite) {
    await db.delete('favourites', id)
    return null
  }
  const existing = await db.get('favourites', id)
  if (existing) return existing
  const record: Favourite = {
    id,
    kataId,
    techniqueId,
    createdAt: nowIso(),
  }
  await db.put('favourites', record)
  return record
}

export async function isFavourite(kataId: KataId, techniqueId: string): Promise<boolean> {
  return Boolean(await getFavourite(kataId, techniqueId))
}
